import axios from 'axios';
import { invalidate, clearAll } from './apiCache';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export const VIEW_DIVISION_KEY = 'viewDivisionId';

// Read-only viewers look at one division at a time — applied to the shared
// instance AND the default axios (some pages still call axios directly).
const addViewDivision = (config) => {
  const div = localStorage.getItem(VIEW_DIVISION_KEY);
  if (div) config.headers['X-View-Division'] = div;
  return config;
};
api.interceptors.request.use(addViewDivision);
axios.interceptors.request.use(addViewDivision);

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      clearAll();  // wipe all cached scoped data on auth loss
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Re-export so callers can bust the cache manually if needed.
export { invalidate as invalidateCache };

/** Helper: chain a mutating API call so its success automatically busts
 * the relevant cache prefix. Keeps cache invalidation co-located with the
 * mutation so call sites don't have to remember. */
const withInvalidate = (prefix, fn) => async (...args) => {
  const result = await fn(...args);
  invalidate(prefix);
  return result;
};

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: async () => {
    const r = await api.post('/auth/logout');
    clearAll();  // belt-and-braces: nuke cache on explicit logout too
    return r;
  },
  me: () => api.get('/auth/me'),
};

// Departments
export const departmentsAPI = {
  list: () => api.get('/departments'),
  create: withInvalidate('master/departments', (data) => api.post('/departments', data)),
  update: withInvalidate('master/departments', (id, data) => api.put(`/departments/${id}`, data)),
  delete: withInvalidate('master/departments', (id) => api.delete(`/departments/${id}`)),
};

// Stations
export const stationsAPI = {
  list: () => api.get('/stations'),
  create: withInvalidate('master/stations', (data) => api.post('/stations', data)),
  update: withInvalidate('master/stations', (id, data) => api.put(`/stations/${id}`, data)),
  delete: withInvalidate('master/stations', (id) => api.delete(`/stations/${id}`)),
};

// Locations
export const locationsAPI = {
  list: (stationIdOrParams) => {
    const params = typeof stationIdOrParams === 'string'
      ? { station_id: stationIdOrParams }
      : stationIdOrParams;
    return api.get('/locations', { params });
  },
  create: withInvalidate('master/locations', (data) => api.post('/locations', data)),
  update: withInvalidate('master/locations', (id, data) => api.put(`/locations/${id}`, data)),
  delete: withInvalidate('master/locations', (id) => api.delete(`/locations/${id}`)),
};

// Sub-Zones (clusters of identical grouped assets within a location)
export const subZonesAPI = {
  list: (params) => api.get('/sub-zones', { params }),
  create: (data) => api.post('/sub-zones', data),
  update: (id, data) => api.put(`/sub-zones/${id}`, data),
  delete: (id, force = false) => api.delete(`/sub-zones/${id}`, { params: { force } }),
  reorder: (locationId, orderedIds) =>
    api.patch('/sub-zones/reorder', { location_id: locationId, ordered_ids: orderedIds }),
  updateSideLabels: (id, labels) => api.put(`/sub-zones/${id}/side-labels`, labels),
};

// Canvas Landmarks (P.No reference markers on Platform Blueprint)
export const canvasLandmarksAPI = {
  list: (params) => api.get('/canvas-landmarks', { params }),
  create: (data) => api.post('/canvas-landmarks', data),
  update: (id, data) => api.put(`/canvas-landmarks/${id}`, data),
  delete: (id) => api.delete(`/canvas-landmarks/${id}`),
};

// Station Canvas (aggregated blueprint data)
export const stationCanvasAPI = {
  get: (params) => api.get('/station-canvas', { params }),
};

// Asset Types
export const assetTypesAPI = {
  list: (departmentId) => api.get('/asset-types', { params: { department_id: departmentId } }),
  create: (data) => api.post('/asset-types', data),
  update: (id, data) => api.put(`/asset-types/${id}`, data),
  delete: (id) => api.delete(`/asset-types/${id}`),
};

// Assets
export const assetsAPI = {
  list: (params) => api.get('/assets', { params }),
  listPaginated: (options = {}) => {
    const params = {
      paginated: true,
      page: options.page || 1,
      page_size: options.pageSize || 50,
    };
    if (options.search) params.search = options.search;
    if (options.station_id) params.station_id = options.station_id;
    if (options.asset_type_id) params.asset_type_id = options.asset_type_id;
    if (options.location_id) params.location_id = options.location_id;
    if (options.sub_zone_id) params.sub_zone_id = options.sub_zone_id;
    if (options.status) params.status = options.status;
    if (options.department_id) params.department_id = options.department_id;
    if (options.assigned_supervisor_id) params.assigned_supervisor_id = options.assigned_supervisor_id;
    if (options.for_user_id) params.for_user_id = options.for_user_id;
    return api.get('/assets', { params });
  },
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  autoCreate: (data) => api.post('/assets/auto-create', data),
  previewCode: (data) => api.post('/assets/preview-code', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
  patchStatus: (id, status) => api.patch(`/assets/${id}/status`, { status }),
  patchCount: (id, count) => api.patch(`/assets/${id}/count`, { total_count: count }),
  inspections: (id, limit) => api.get(`/assets/${id}/inspections`, { params: { limit } }),
  markDefective: (id, data) => api.post(`/assets/${id}/mark-defective`, data),
  bulkAssignSubZone: (assetIds, subZoneId) =>
    api.patch('/assets/bulk/sub-zone', { asset_ids: assetIds, sub_zone_id: subZoneId || null }),
  bulkUpdateCanvasPositions: (positions, userId) =>
    api.patch('/assets/bulk/canvas', { positions }, { params: { current_user_id: userId || '' } }),
  updateCanvasPosition: (assetId, canvasX, canvasY, userId) =>
    api.patch(`/assets/${assetId}/canvas`, { canvas_x: canvasX, canvas_y: canvasY }, { params: { current_user_id: userId || '' } }),
};

// Users
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  grantAdmin: (userId, grantedBy) => api.post(`/users/${userId}/grant-admin?granted_by=${grantedBy}`),
  revokeAdmin: (userId, revokedBy, newRole) => api.post(`/users/${userId}/revoke-admin?revoked_by=${revokedBy}&new_role=${newRole}`),
  supervisors: (params) => api.get('/users/supervisors', { params }),
  inspections: (id, limit) => api.get(`/users/${id}/inspections`, { params: { limit } }),
  linkSupervisors: (reportingOfficerId, supervisorIds) => api.post('/users/link-supervisors', {
    reporting_officer_id: reportingOfficerId,
    supervisor_ids: supervisorIds
  }),
  stationStaff: () => api.get('/users/station-staff'),
};

// Admin audit trail
export const auditAPI = {
  trail: (params) => api.get('/admin/audit-trail', { params }),
};

// Inspections
export const inspectionsAPI = {
  list: (params) => api.get('/inspections', { params }),
  listPaginated: (options = {}) => {
    const params = {
      paginated: true,
      page: options.page || 1,
      page_size: options.pageSize || 25,
    };
    if (options.station_id) params.station_id = options.station_id;
    if (options.inspector_id) params.inspector_id = options.inspector_id;
    if (options.inspection_type) params.inspection_type = options.inspection_type;
    if (options.for_user_id) params.for_user_id = options.for_user_id;
    return api.get('/inspections', { params });
  },
  get: (id) => api.get(`/inspections/${id}`),
  create: (data) => api.post('/inspections', data),
  update: (id, data) => api.patch(`/inspections/${id}`, data),
  finalize: (id, finalizedBy) =>
    api.post(`/inspections/${id}/finalize${finalizedBy ? `?finalized_by=${finalizedBy}` : ''}`),
  stationSupervisors: (stationId) =>
    api.get('/users/supervisors', { params: { station_id: stationId } }),
  // Returns the full backend URL for the SIG Summary PDF (used as <a href=>)
  sigSummaryPdfUrl: (id) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${id}/sig-summary.pdf`,
  sigSummaryData: (id) => api.get(`/inspections/${id}/sig-summary-data`),
};

// Orange List
export const orangeListAPI = {
  list: (params) => api.get('/orange-list', { params }),
  summary: (params) => api.get('/orange-list/summary', { params }),
  listPaginated: (options = {}) => {
    const params = {
      paginated: true,
      page: options.page || 1,
      page_size: options.pageSize || 25,
    };
    if (options.list_type) params.list_type = options.list_type;
    if (options.for_user_id) params.for_user_id = options.for_user_id;
    if (options.station_id) params.station_id = options.station_id;
    if (options.department_id) params.department_id = options.department_id;
    return api.get('/orange-list', { params });
  },
  markWorking: (id, data) => api.post(`/orange-list/${id}/mark-working`, data),
  approve: (id, data) => api.post(`/orange-list/${id}/approve`, data),
  rejectWorking: (id, data) => api.post(`/orange-list/${id}/reject-working`, data),
};

// Remarks (Phase 5 — threaded log)
export const remarksAPI = {
  listTags: (includeArchived = false) =>
    api.get('/remarks/tags', { params: { include_archived: includeArchived } }),
  createTag: (data, currentUserId) =>
    api.post('/remarks/tags', data, { params: { current_user_id: currentUserId } }),
  updateTag: (id, data, currentUserId) =>
    api.put(`/remarks/tags/${id}`, data, { params: { current_user_id: currentUserId } }),
  deleteTag: (id, currentUserId) =>
    api.delete(`/remarks/tags/${id}`, { params: { current_user_id: currentUserId } }),
  listThread: (orangeListId) =>
    api.get(`/orange-list/${orangeListId}/remarks`),
  postRemark: (orangeListId, data) =>
    api.post(`/orange-list/${orangeListId}/remarks`, data),
};

// Notifications
export const notificationsAPI = {
  list: (userId, unreadOnly) => api.get('/notifications', { params: { user_id: userId, unread_only: unreadOnly } }),
  // Paginated list for the full Notifications page
  listPaginated: (userId, options = {}) => {
    const params = {
      user_id: userId,
      paginated: true,
      page: options.page || 1,
      page_size: options.pageSize || 20,
    };
    if (options.unreadOnly) params.unread_only = true;
    if (options.search) params.search = options.search;
    if (options.notificationType) params.notification_type = options.notificationType;
    if (options.fromDate) params.from_date = options.fromDate;
    if (options.toDate) params.to_date = options.toDate;
    return api.get('/notifications', { params });
  },
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markUnread: (id) => api.post(`/notifications/${id}/unread`),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteRead: (userId) => api.post(`/notifications/delete-read?user_id=${userId}`),
  markAllRead: (userId) => api.post(`/notifications/mark-all-read?user_id=${userId}`),
  unreadCount: (userId) => api.get(`/notifications/unread-count?user_id=${userId}`),
};

// Dashboard / Analytics
export const analyticsAPI = {
  supervisor: (userId) => api.get(`/analytics/supervisor/${userId}`),
  supervisorPerformance: (userId, params = {}) => {
    const p = new URLSearchParams();
    if (params.fromDate) p.set('from_date', params.fromDate);
    if (params.toDate) p.set('to_date', params.toDate);
    if (params.stationId) p.set('station_id', params.stationId);
    if (params.locationId) p.set('location_id', params.locationId);
    return api.get(`/analytics/supervisor/${userId}/performance?${p.toString()}`);
  },
  asupPerformanceSummary: (userId, params = {}) => {
    const p = new URLSearchParams();
    if (params.fromDate) p.set('from_date', params.fromDate);
    if (params.toDate) p.set('to_date', params.toDate);
    return api.get(`/analytics/approving-supervisor/${userId}/performance-summary?${p.toString()}`);
  },
  roPerformanceSummary: (userId, params = {}) => {
    const p = new URLSearchParams();
    if (params.fromDate) p.set('from_date', params.fromDate);
    if (params.toDate) p.set('to_date', params.toDate);
    return api.get(`/analytics/reporting-officer/${userId}/performance-summary?${p.toString()}`);
  },
  approvingSupervisorList: (userId) => api.get(`/analytics/approving-supervisor/${userId}/supervisors`),
  asset: (assetId) => api.get(`/analytics/asset/${assetId}`),
  adminRollup: (params = {}) => {
    const p = new URLSearchParams();
    if (params.fromDate) p.set('from_date', params.fromDate);
    if (params.toDate) p.set('to_date', params.toDate);
    // if (params.currentUserId) p.set('current_user_id', params.currentUserId);
    return api.get(`/analytics/admin/rollup?${p.toString()}`);
  },
  adminCoverageGaps: () =>
    api.get('/analytics/admin/coverage-gaps'),
};

export const approvalsAPI = {
  pending: (reviewerId) => api.get('/inspections/pending-approvals', { params: { reviewer_id: reviewerId } }),
  approve: (inspectionId, itemIndex, remarks) =>
    api.post(`/inspections/${inspectionId}/items/${itemIndex}/approve`, { remarks }),
  reject: (inspectionId, itemIndex, remarks) =>
    api.post(`/inspections/${inspectionId}/items/${itemIndex}/reject`, { remarks }),
};

// Schedules
export const schedulesAPI = {
  list: (overdueOnly) => api.get('/schedules', { params: { overdue_only: overdueOnly } }),
  dueToday: (userId) => api.get('/schedules/due-today', { params: { user_id: userId } }),
  create: (data) => api.post('/schedules', data),
  forSupervisor: (userId, fromDate, toDate) => api.get(`/schedules/supervisor/${userId}`, {
    params: { from_date: fromDate, to_date: toDate }
  }),
  supervisorsUnderApproving: (userId) => api.get(`/schedules/approving-supervisor/${userId}/supervisors`),
  admin: (filters) => {
    // filters: { station_ids: [], department_ids: [], asset_type_ids: [], supervisor_ids: [], reporting_officer_ids: [], from_date, to_date }
    const p = new URLSearchParams();
    (filters.station_ids || []).forEach((v) => p.append('station_ids', v));
    (filters.department_ids || []).forEach((v) => p.append('department_ids', v));
    (filters.asset_type_ids || []).forEach((v) => p.append('asset_type_ids', v));
    (filters.supervisor_ids || []).forEach((v) => p.append('supervisor_ids', v));
    (filters.reporting_officer_ids || []).forEach((v) => p.append('reporting_officer_ids', v));
    if (filters.from_date) p.append('from_date', filters.from_date);
    if (filters.to_date) p.append('to_date', filters.to_date);
    return api.get(`/schedules/admin?${p.toString()}`);
  },
};

// Admin
export const adminAPI = {
  transferSupervisor: (fromSupervisorId, toSupervisorId) => api.post('/admin/transfer-supervisor', {
    from_supervisor_id: fromSupervisorId,
    to_supervisor_id: toSupervisorId,
  }),
  assignAssetsBulk: (assetIds, toSupervisorId) => api.post('/admin/assets/assign-bulk', {
    asset_ids: assetIds,
    to_supervisor_id: toSupervisorId || null,
  }),
};

// Data Health (admin / superadmin)
export const dataHealthAPI = {
  canvasMismatches: () => api.get('/data-health/canvas-location-mismatches'),
  reconcileCanvas: (assetIds) => api.post('/data-health/canvas-location-mismatches/reconcile', { asset_ids: assetIds || null }),
};

// Worst Affected Sub-Zones (Reports)
export const worstSubZonesAPI = {
  list: (params) => api.get('/reports/worst-subzones', { params }),
  pdfBlob: (params) => api.get('/reports/worst-subzones/pdf', { params, responseType: 'blob' }),
};

// Sub-zone surface layers (floor / roof panel health)
export const subzoneSurfacesAPI = {
  get: (subZoneId) => api.get(`/subzone-surfaces/${subZoneId}`),
  batch: (subZoneIds) => api.get('/subzone-surfaces-batch', { params: { ids: (subZoneIds || []).join(',') } }),
  markDamage: (subZoneId, layer, panelIds, extra = {}) => api.post(`/subzone-surfaces/${subZoneId}/${layer}/mark-damage`, { panel_ids: panelIds, ...extra }),
  repairPanels: (subZoneId, layer, panelIds, remark) => api.post(`/subzone-surfaces/${subZoneId}/${layer}/repair-panels`, { panel_ids: panelIds, remark }),
  markWorking: (subZoneId, layer, body) => api.post(`/subzone-surfaces/${subZoneId}/${layer}/mark-working`, body || {}),
  approve: (subZoneId, layer) => api.post(`/subzone-surfaces/${subZoneId}/${layer}/approve`),
  addNote: (subZoneId, layer, payload) => api.post(`/subzone-surfaces/${subZoneId}/${layer}/note`, payload),
  history: (subZoneId, layer) => api.get(`/subzone-surfaces/${subZoneId}/${layer}/history`),
  runDigest: () => api.post('/subzone-surfaces/digest/run'),
  config: (subZoneId, layer, payload) => api.put(`/subzone-surfaces/${subZoneId}/${layer}/config`, payload),
  assign: (subZoneId, deptId) => api.put(`/subzone-surfaces/${subZoneId}/assign`, { dept_id: deptId || null }),
};

// Dashboard
export const dashboardAPI = {
  superadmin: () => api.get('/dashboard'),
  superadminFull: (filters) => {
    const p = new URLSearchParams();
    (filters?.station_ids || []).forEach((v) => p.append('station_ids', v));
    const qs = p.toString();
    return api.get(`/dashboard/superadmin${qs ? `?${qs}` : ''}`);
  },
  stats: () => api.get('/dashboard/stats'),
  recentInspections: (limit) => api.get('/dashboard/recent-inspections', { params: { limit } }),
  supervisor: (userId, stationId) => api.get(`/dashboard/supervisor/${userId}`, {
    params: stationId ? { station_id: stationId } : {}
  }),
  supervisorMyTasks: (userId, stationId) => api.get(`/dashboard/supervisor/${userId}/my-tasks`, {
    params: stationId ? { station_id: stationId } : {}
  }),
  approvingSupervisor: (userId, params) => api.get(`/dashboard/approving-supervisor/${userId}`, {
    params: params || {}
  }),
  reportingOfficer: (userId, params) => api.get(`/dashboard/reporting-officer/${userId}`, {
    params: params || {}
  }),
  oversightCategoryAssets: (userId, params) => api.get(`/dashboard/oversight/${userId}/category-assets`, {
    params: params || {}
  }),
  admin: (filters) => {
    const p = new URLSearchParams();
    (filters?.station_ids || []).forEach((v) => p.append('station_ids', v));
    (filters?.department_ids || []).forEach((v) => p.append('department_ids', v));
    (filters?.reporting_officer_ids || []).forEach((v) => p.append('reporting_officer_ids', v));
    return api.get(`/dashboard/admin?${p.toString()}`);
  },
};

// Upload
export const uploadAPI = {
  single: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  multiple: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/upload/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Profiles
export const profilesAPI = {
  get: (userId, params = {}) => {
    const p = new URLSearchParams();
    if (params.dept_id) p.set('dept_id', params.dept_id);
    if (params.station_id) p.set('station_id', params.station_id);
    return api.get(`/profiles/${userId}?${p.toString()}`);
  },
};

// Zones
const _uid = () => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u._id || u.id || ''; } catch { return ''; } };

export const zonesAPI = {
  list: () => api.get('/zones'),
  create: withInvalidate('master/zones', (data) => api.post(`/zones?current_user_id=${_uid()}`, data)),
  update: withInvalidate('master/zones', (id, data) => api.put(`/zones/${id}?current_user_id=${_uid()}`, data)),
  delete: withInvalidate('master/zones', (id) => api.delete(`/zones/${id}?current_user_id=${_uid()}`)),
};

// Inspection Compliance
export const complianceAPI = {
  supervisorActivity: (userId, params = {}) => api.get(`/inspection-compliance/supervisor-activity/${userId}`, { params }),
  missingHeatmap: (userId) => api.get(`/inspection-compliance/missing-heatmap/${userId}`),
  sigHistory: (userId, params = {}) => api.get(`/inspection-compliance/sig-history/${userId}`, { params }),
  exportSigPdf: (inspectionId) => api.post(`/inspection-compliance/sig/${inspectionId}/export/pdf`, {}, { responseType: 'blob' }),
  getThreshold: () => api.get('/settings/compliance-threshold'),
  updateThreshold: (data) => api.put('/settings/compliance-threshold', data),
};

// Divisions
export const divisionsAPI = {
  list: () => api.get('/divisions'),
  get: (id) => api.get(`/divisions/${id}`),
  create: withInvalidate('master/divisions', (data) => api.post(`/divisions?current_user_id=${_uid()}`, data)),
  update: withInvalidate('master/divisions', (id, data) => api.put(`/divisions/${id}?current_user_id=${_uid()}`, data)),
  delete: withInvalidate('master/divisions', (id) => api.delete(`/divisions/${id}?current_user_id=${_uid()}`)),
  getStations: (id) => api.get(`/divisions/${id}/stations`),
  assignStations: withInvalidate('master/stations', (id, station_ids) =>
    api.post(`/divisions/${id}/assign-stations?current_user_id=${_uid()}`, station_ids)),
};


// SIG Sessions (Location-Atomic Auto-Commit)
export const sigSessionsAPI = {
  start: (data) => api.post('/sig-sessions/start', data),
  active: (params) => api.get('/sig-sessions/active', { params }),
  ongoing: () => api.get('/sig-sessions/ongoing'),
  terminate: (id) => api.post(`/sig-sessions/${id}/terminate`),
  byUser: (userId) => api.get(`/sig-sessions/by-user/${userId}`),
  get: (id) => api.get(`/sig-sessions/${id}`),
  upsertItem: (id, item) => api.post(`/sig-sessions/${id}/upsert-item`, item),
  addLocation: (id, locationId) => api.post(`/sig-sessions/${id}/add-location`, { location_id: locationId }),
  deleteItem: (id, assetId) => api.delete(`/sig-sessions/${id}/item/${assetId}`),
  deleteGroup: (data) => api.post('/sig-sessions/delete-group', data),
  finalize: (id, data) => api.post(`/sig-sessions/${id}/finalize`, data),
  coverage: (id) => api.get(`/sig-sessions/${id}/coverage`),
  summaryData: (id) => api.get(`/sig-sessions/${id}/summary-data`),
  summaryPdfUrl: (id) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/sig-sessions/${id}/summary.pdf`;
  },
};

// Activity Bundle (consolidated "what is this user doing right now")
export const activityBundleAPI = {
  get: (userId) => api.get('/me/activity-bundle', { params: { user_id: userId } }),
};

// Asset Reservations (soft-lock during ongoing inspections)
export const reservationsAPI = {
  reserve: (data) => api.post('/asset-reservations', data),
  release: (data) => api.delete('/asset-reservations', { data }),
  list: (params) => api.get('/asset-reservations', { params }),
};

// Visual Inspection Report PDF (programmatic blueprint + defect dots)
export const visualReportAPI = {
  pdfUrl: ({ stationId, locationId, assetTypeId }) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams({ station_id: stationId });
    if (locationId) params.set('location_id', locationId);
    if (assetTypeId) params.set('asset_type_id', assetTypeId);
    return `${base}/api/reports/visual-inspection.pdf?${params.toString()}`;
  },
};

// Station Status Snapshot — per-station unit-level report (PDF / Excel / JSON)
export const stationSnapshotAPI = {
  json: (stationId) => api.get(`/reports/station-snapshot/${stationId}`),
  pdfUrl: (stationId) => `${process.env.REACT_APP_BACKEND_URL}/api/reports/station-snapshot/${stationId}/pdf`,
  excelUrl: (stationId) => `${process.env.REACT_APP_BACKEND_URL}/api/reports/station-snapshot/${stationId}/excel`,
};

// QR Code generation
export const qrAPI = {
  subzonePngUrl: (subzoneId, baseUrl) => {
    const params = new URLSearchParams({ base_url: baseUrl || window.location.origin });
    return `${process.env.REACT_APP_BACKEND_URL}/api/qr/subzone/${subzoneId}/png?${params}`;
  },
  stationSheetPdfUrl: (stationId, baseUrl) => {
    const params = new URLSearchParams({ base_url: baseUrl || window.location.origin });
    return `${process.env.REACT_APP_BACKEND_URL}/api/qr/station/${stationId}/sheet/pdf?${params}`;
  },
  subzoneInfo: (subzoneId) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qr/subzone/${subzoneId}/info`).then(r => r.json()),
};

// Defect SLAs (per Station × Asset Type)
export const defectSlasAPI = {
  list: (params) => api.get('/defect-slas', { params }),
  upsert: (data) => api.put('/defect-slas', data),
  reset: (stationId, assetTypeId) => api.delete(`/defect-slas/${stationId}/${assetTypeId}`),
};


// Public sub-zone assets (no auth)
export const publicAPI = {
  subzoneAssets: (subzoneId) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/subzone/${subzoneId}/assets`).then(r => r.json()),
  submitComplaint: (data) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/complaints`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
  getTicketStatus: (ticketId) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/complaints/${ticketId}`).then(r => r.json()),
  subzoneSurfaces: (subzoneId) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/subzone/${subzoneId}/surfaces`).then(r => r.json()),
  reportSurface: (subzoneId, data) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/subzone/${subzoneId}/surface-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e))),
  verifyComplaint: (ticketId, action, userId) => api.patch(`/admin/public-complaints/${ticketId}/verify?action=${action}`, {}, { params: { current_user_id: userId } }),
  listComplaints: (params) => api.get('/admin/public-complaints', { params }),
};

// AI Insights (zero-cost hybrid Q&A)
export const aiInsightsAPI = {
  templates: () => api.get('/ai-insights/templates'),
  autocomplete: (q) => api.get('/ai-insights/autocomplete', { params: { q } }),
  query: (data) => api.post('/ai-insights/query', data),
  history: () => api.get('/ai-insights/history'),
  deleteHistoryItem: (id) => api.delete(`/ai-insights/history/${id}`),
  clearHistory: () => api.delete('/ai-insights/history'),
  telemetrySummary: (range = '7d') => api.get('/ai-insights/telemetry/summary', { params: { range } }),
  telemetryTrend: (range = '30d') => api.get('/ai-insights/telemetry/trend', { params: { range } }),
  telemetryTopQueries: (source, range = '30d', limit = 20) => api.get('/ai-insights/telemetry/top-queries', { params: { source, range, limit } }),
  telemetryByEndpoint: (range = '7d') => api.get('/ai-insights/telemetry/by-endpoint', { params: { range } }),
  telemetryClear: () => api.delete('/ai-insights/telemetry'),
};

// Backend Performance (slow-query log observability)
export const perfAPI = {
  summary: (range = '7d') => api.get('/perf/summary', { params: { range } }),
  byPath: (range = '7d') => api.get('/perf/by-path', { params: { range } }),
  recent: (path = '', limit = 20) => api.get('/perf/recent', { params: { path, limit } }),
  clear: () => api.delete('/perf/log'),
};



